import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient, withMinDelayThunk } from 'utils/api';

// Thunks
export const hydrateUser = createAsyncThunk(
  'auth/hydrateUser',
  withMinDelayThunk(async (_, { rejectWithValue }) => {
    try {
      const res = await apiClient.get('/auth/me');
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Not authenticated');
    }
  }, 3000)
);

export const login = createAsyncThunk(
  'auth/login',
  withMinDelayThunk(async (credentials, { _dispatch, rejectWithValue }) => {
    try {
      const res = await apiClient.post('/auth/login', credentials);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || 'Login failed');
    }
  })
);

export const register = createAsyncThunk(
  'auth/register',
  withMinDelayThunk(async (data, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/auth/register', data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Registration failed');
    }
  })
);

export const activate = createAsyncThunk(
  'auth/activate',
  withMinDelayThunk(async (activationCode, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/auth/activate', { activationCode });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Activation failed');
    }
  })
);

export const logout = createAsyncThunk(
  'auth/logout',
  withMinDelayThunk(async (_, { rejectWithValue }) => {
    try {
      await apiClient.post('/auth/logout');
      return null;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Logout failed');
    }
  }, 3000)
);

// Forgot Password
export const forgotPassword = createAsyncThunk(
  'auth/forgot',
  withMinDelayThunk(async (email, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/auth/forgot', { email });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to send reset email');
    }
  })
);

// Reset Password
export const resetPassword = createAsyncThunk(
  'auth/reset',
  withMinDelayThunk(async ({ token, password }, { rejectWithValue }) => {
    try {
      const res = await apiClient.post('/auth/reset', { token, password });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to reset password');
    }
  })
);

export const updateSettings = createAsyncThunk(
  'auth/updateSettings',
  withMinDelayThunk(async ({ userId, settings }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/users/${userId}/settings`, settings);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update settings');
    }
  })
);

const initialState = {
  user: null,
  loading: true,
  error: null,
  status: null,
  init: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearAuthError(state) {
      state.error = null;
      state.status = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Hydrate user
      .addCase(hydrateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.init = false;
      })
      .addCase(hydrateUser.fulfilled, (state, action) => {
        state.user = action.payload.data;
        state.loading = false;
        state.init = true;
      })
      .addCase(hydrateUser.rejected, (state) => {
        state.user = null;
        state.loading = false;
        state.init = true;
      })
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.user = null;
        state.loading = false;
        state.error = action.payload.error || 'Login failed';
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.status = action.payload?.message || 'Registration successful.';
        state.loading = false;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Activate
      .addCase(activate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(activate.fulfilled, (state, action) => {
        state.status = action.payload?.message || 'Activation successful.';
        state.loading = false;
      })
      .addCase(activate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Forgot Password
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.status = action.payload?.message || 'Reset email sent.';
        state.loading = false;
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Reset Password
      .addCase(resetPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resetPassword.fulfilled, (state, action) => {
        state.status = action.payload?.message || 'Password was changed successfully.';
        state.loading = false;
      })
      .addCase(resetPassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.loading = false;
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Settings
      .addCase(updateSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.status = action.payload?.message || 'Settings updated successfully.';
        if (state.user) {
          state.user.settings = action.payload.data.settings;
        }
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const selectSettings = (state) => state.auth.user?.settings;

export const { clearAuthError, setInit } = authSlice.actions;
export default authSlice.reducer;
